import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  type: ToastType
  message: string
}

interface ToastContextValue {
  toast: (type: ToastType, message: string) => void
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS: Record<ToastType, number | null> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: null,
}

const STYLES: Record<ToastType, { bg: string; border: string; icon: string; text: string }> = {
  success: { bg: 'bg-emerald-950', border: 'border-emerald-700', icon: '✓', text: 'text-emerald-300' },
  error:   { bg: 'bg-red-950',     border: 'border-red-700',     icon: '✕', text: 'text-red-300'     },
  warning: { bg: 'bg-amber-950',   border: 'border-amber-700',   icon: '⚠', text: 'text-amber-300'   },
  info:    { bg: 'bg-slate-800',   border: 'border-slate-600',   icon: 'ℹ', text: 'text-slate-300'   },
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const s = STYLES[toast.type]
  const autoDismiss = AUTO_DISMISS[toast.type]

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  useEffect(() => {
    if (autoDismiss == null) return
    timerRef.current = setTimeout(() => dismiss(), autoDismiss)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [autoDismiss])

  function dismiss() {
    setVisible(false)
    setTimeout(() => onRemove(toast.id), 300)
  }

  return (
    <div
      className={`
        flex items-start gap-3 rounded-lg border px-4 py-3 shadow-xl max-w-sm w-full
        transition-all duration-300 ease-out cursor-default
        ${s.bg} ${s.border}
        ${visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <span className={`text-base font-bold mt-0.5 flex-shrink-0 ${s.text}`}>{s.icon}</span>
      <p className={`flex-1 text-sm leading-snug ${s.text}`}>{toast.message}</p>
      <button
        onClick={dismiss}
        className="flex-shrink-0 text-slate-500 hover:text-slate-300 transition-colors mt-0.5"
        aria-label="Închide"
      >
        <X size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = `${Date.now()}-${Math.random()}`
    setToasts(prev => {
      const next = [...prev, { id, type, message }]
      return next.length > 4 ? next.slice(next.length - 4) : next
    })
  }, [])

  const ctx: ToastContextValue = {
    toast: addToast,
    success: (m) => addToast('success', m),
    error:   (m) => addToast('error', m),
    warning: (m) => addToast('warning', m),
    info:    (m) => addToast('info', m),
  }

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onRemove={remove} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast trebuie folosit în interiorul ToastProvider')
  return ctx
}
